import type { ItineraryItem, PlacePhoto } from '@/types/itinerary';
import { API_BASE } from './apiBase';
import { foursquarePhotoUrl } from './places/foursquare';

export interface NearbySearchParams {
  lat: number;
  lng: number;
  radiusMeters: number;
  /**
   * FSQ-only supplemental subcategory IDs to fetch. Omit to let the server use
   * its default selection (subcategories whose group is `enabledByDefault`).
   */
  supplementalCategoryIds?: Array<string>;
}

interface SearchResponse {
  places: Array<ItineraryItem>;
  /** Server is still ingesting missing cells in the background — results may
   *  keep filling in, so the caller should poll again shortly. */
  pending?: boolean;
}

export interface NearbySearchResult {
  items: Array<ItineraryItem>;
  pending: boolean;
}

export const searchNearby = async ({
  lat,
  lng,
  radiusMeters,
  supplementalCategoryIds,
}: NearbySearchParams): Promise<NearbySearchResult> => {
  const response = await fetch(`${API_BASE}/api/search`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      lat,
      lng,
      radiusMeters,
      ...(supplementalCategoryIds ? { supplementalCategoryIds } : {}),
    }),
  });

  if (!response.ok) {
    const body = await response.text();
    throw new Error(`Search failed: ${response.status} — ${body}`);
  }

  // The server aggregates + normalizes across providers and returns finished
  // ItineraryItems, so there's no per-provider transform here anymore.
  // Defensively drop any lacking valid coords (would crash the map renderer).
  const data = (await response.json()) as SearchResponse;
  return {
    items: data.places.filter(
      (item) => Number.isFinite(item.latitude) && Number.isFinite(item.longitude),
    ),
    pending: Boolean(data.pending),
  };
};

export interface InvalidateAreaParams {
  lat: number;
  lng: number;
  radiusMeters: number;
}

export const invalidateArea = async ({
  lat,
  lng,
  radiusMeters,
}: InvalidateAreaParams): Promise<{ invalidated: number }> => {
  const response = await fetch(`${API_BASE}/api/invalidate-area`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ lat, lng, radiusMeters }),
  });
  if (!response.ok) {
    const body = await response.text();
    throw new Error(`Invalidate failed: ${response.status} — ${body}`);
  }
  return (await response.json()) as { invalidated: number };
};

// Resolve a normalized photo to a sized URL. Foursquare photos build from
// prefix/suffix tokens; the openly-licensed fallbacks carry a ready URL. Only
// Wikimedia's `Special:FilePath` endpoint supports server-side resizing, so we
// append `?width=` there and use every other URL as-is.
export const photoUrl = (photo: PlacePhoto, width: number): string => {
  if (photo.provider === 'foursquare') return foursquarePhotoUrl(photo, width);
  if (photo.provider === 'wikimedia' && photo.url.includes('Special:FilePath')) {
    return `${photo.url}?width=${width}`;
  }
  return photo.url;
};
